import { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import LogService, { LOG_ACTIONS } from '../services/LogService';

function TabDownload({ data = [], resumo = {}, fileName = '' }) {
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadExcel = () => {
    setDownloadingExcel(true);

    setTimeout(() => {
      const wsData = [
        ['Quanto Custa Obra - Quantitativos da Obra'],
        [`Arquivo: ${fileName}`, `Tipo: ${resumo?.tipo_projeto || ''}`, `Área Total: ${resumo?.area_total || '-'} m²`],
        [],
        ['Elemento', 'Categoria', 'Quantidade', 'Unidade'],
        ...data.map((item) => [
          item.elemento,
          item.categoria,
          item.quantidade,
          item.unidade,
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = [
        { wch: 30 },
        { wch: 15 },
        { wch: 15 },
        { wch: 10 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Quantitativos');
      const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, 'QuantoCustaObra_Quantitativos.xlsx');
      LogService.log(LOG_ACTIONS.DOWNLOAD_EXCEL, {
        details: { fileName: 'QuantoCustaObra_Quantitativos.xlsx', itemCount: data.length },
      });

      setDownloadingExcel(false);
    }, 800);
  };

  const handleDownloadPdf = () => {
    setDownloadingPdf(true);

    setTimeout(() => {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let y = 15;

      // Header
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageWidth, 35, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('QuantoCustaObra', margin, 16);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('quantocustaobra.com.br', margin, 23);
      doc.text(new Date().toLocaleDateString('pt-BR'), pageWidth - margin, 16, { align: 'right' });
      y = 45;

      // Title
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Relatorio de Quantitativos', margin, y);
      y += 8;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(resumo?.descricao || 'Analise gerada por inteligencia artificial', margin, y);
      y += 6;
      doc.text(`Arquivo: ${fileName || '-'} | Tipo: ${resumo?.tipo_projeto || '-'} | Area: ${resumo?.area_total || '-'} m2`, margin, y);
      y += 8;

      // Table
      doc.autoTable({
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Elemento', 'Categoria', 'Quantidade', 'Unidade']],
        body: data.map((item) => [
          item.elemento,
          item.categoria,
          typeof item.quantidade === 'number' ? item.quantidade.toLocaleString('pt-BR') : item.quantidade,
          item.unidade,
        ]),
        theme: 'striped',
        headStyles: { fillColor: [15, 23, 42], fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 2: { halign: 'right' } },
      });

      // Footer
      const footerY = doc.internal.pageSize.getHeight() - 15;
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text('Gerado por QuantoCustaObra.com.br', margin, footerY);

      doc.save('QuantoCustaObra_Quantitativos.pdf');
      LogService.log(LOG_ACTIONS.DOWNLOAD_PDF, {
        details: { fileName: 'QuantoCustaObra_Quantitativos.pdf', itemCount: data.length },
      });
      setDownloadingPdf(false);
    }, 300);
  };

  return (
    <div className="p-4 md:p-8">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Downloads</h2>
      <p className="text-gray-500 mb-8">
        Baixe o orçamento em Excel ou PDF
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Card Excel */}
        <button
          onClick={handleDownloadExcel}
          disabled={downloadingExcel || data.length === 0}
          className="group p-8 rounded-2xl border-2 border-gray-100 hover:border-green-300 hover:bg-green-50/30 transition-all duration-300 text-left cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
            <svg className="w-8 h-8 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-1">
            {downloadingExcel ? 'Gerando...' : 'Baixar Excel'}
          </h3>
          <p className="text-gray-500 text-sm">
            Planilha com quantitativos completos (.xlsx)
          </p>
          <div className="mt-4 flex items-center gap-2 text-green-700 font-medium">
            {downloadingExcel ? (
              <div className="w-5 h-5 border-2 border-green-700 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
            QuantoCustaObra_Quantitativos.xlsx
          </div>
        </button>

        {/* Card PDF */}
        <button
          onClick={handleDownloadPdf}
          disabled={downloadingPdf || data.length === 0}
          className="group p-8 rounded-2xl border-2 border-gray-100 hover:border-red-300 hover:bg-red-50/30 transition-all duration-300 text-left cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
            <svg className="w-8 h-8 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-1">
            {downloadingPdf ? 'Gerando...' : 'Baixar PDF'}
          </h3>
          <p className="text-gray-500 text-sm">
            Relatório formatado para impressão (.pdf)
          </p>
          <div className="mt-4 flex items-center gap-2 text-red-700 font-medium">
            {downloadingPdf ? (
              <div className="w-5 h-5 border-2 border-red-700 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
            QuantoCustaObra_Quantitativos.pdf
          </div>
        </button>
      </div>
    </div>
  );
}

export default TabDownload;
